import { useState, useEffect } from 'react'
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { authApi } from '../api/auth'

declare const __APP_VERSION__: string

// ── Icons (Material Symbols Outlined, self-hosted path data — no webfont, no
// runtime Google Fonts dependency, same render cost as the hand-drawn set it
// replaces. Paths verified against google/material-design-icons directly,
// not guessed: viewBox is fixed at 0 -960 960 960 for every Material Symbol,
// and they're filled shapes, not stroked outlines like the old Heroicons set
// was — hence fill="currentColor" here instead of stroke.) ────────────────────

function Icon({ path, className = 'w-5 h-5' }: { path: string; className?: string }) {
  return (
    <svg className={className} viewBox="0 -960 960 960" fill="currentColor">
      <path d={path} />
    </svg>
  )
}

const ICONS = {
  dashboard: 'M520-600v-240h320v240H520ZM120-440v-400h320v400H120Zm400 320v-400h320v400H520Zm-400 0v-240h320v240H120Zm80-400h160v-240H200v240Zm400 320h160v-240H600v240Zm0-480h160v-80H600v80ZM200-200h160v-80H200v80Zm160-320Zm240-160Zm0 240ZM360-280Z',
  scada:     'M771-593 630-734l-85 84-85-84 113-114q12-12 27-17.5t30-5.5q16 0 30.5 5.5T686-848l85 85q18 17 26.5 39.5T806-678q0 23-8.5 45T771-593ZM220-409q-18-18-18-42.5t18-42.5l98-99 85 85-99 99q-17 18-41.5 18T220-409Zm-43 297q-11-12-17-26.5t-6-30.5q0-16 5.5-30.5T177-226l283-282-127-128q-18-17-18-41.5t18-42.5q17-18 42-18t43 18l127 127 57-57 112 114q12 12 12 28t-12 28q-12 12-28 12t-28-12L290-112q-12 12-26.5 17.5T234-89q-15 0-30-6t-27-17Z',
  routes:    'M360-120q-66 0-113-47t-47-113v-327q-35-13-57.5-43.5T120-720q0-50 35-85t85-35q50 0 85 35t35 85q0 39-22.5 69.5T280-607v327q0 33 23.5 56.5T360-200q33 0 56.5-23.5T440-280v-400q0-66 47-113t113-47q66 0 113 47t47 113v327q35 13 57.5 43.5T840-240q0 50-35 85t-85 35q-50 0-85-35t-35-85q0-39 22.5-70t57.5-43v-327q0-33-23.5-56.5T600-760q-33 0-56.5 23.5T520-680v400q0 66-47 113t-113 47ZM240-680q17 0 28.5-11.5T280-720q0-17-11.5-28.5T240-760q-17 0-28.5 11.5T200-720q0 17 11.5 28.5T240-680Zm480 480q17 0 28.5-11.5T760-240q0-17-11.5-28.5T720-280q-17 0-28.5 11.5T680-240q0 17 11.5 28.5T720-200ZM240-720Zm480 480Z',
  meters:    'M295-119q-36-1-68.5-18.5T165-189q-40-48-62.5-114.5T80-440q0-83 31.5-156T197-723q54-54 127-85.5T480-840q83 0 156 32t127 87q54 55 85.5 129T880-433q0 77-25 144t-71 113q-28 28-59 42.5T662-119q-18 0-36-4.5T590-137l-56-28q-12-6-25.5-9t-28.5-3q-15 0-28.5 3t-25.5 9l-56 28q-19 10-37.5 14.5T295-119Zm2-80q9 0 18.5-2t18.5-7l56-28q21-11 43.5-16t45.5-5q23 0 46 5t44 16l57 28q9 5 18 7t18 2q19 0 36-10t34-30q32-38 50-91t18-109q0-134-93-227.5T480-760q-134 0-227 94t-93 228q0 57 18.5 111t51.5 91q17 20 33 28.5t34 8.5Zm183-281Zm0 120q33 0 56.5-23.5T560-440q0-8-1.5-16t-4.5-16l50-67q10 13 17.5 27.5T634-480h82q-15-88-81.5-144T480-680q-88 0-155 56.5T244-480h82q14-54 57-87t97-33q17 0 32 3t29 9l-51 69q-2 0-5-.5t-5-.5q-33 0-56.5 23.5T400-440q0 33 23.5 56.5T480-360Z',
  analytics: 'M120-120v-80l80-80v160h-80Zm160 0v-240l80-80v320h-80Zm160 0v-320l80 81v239h-80Zm160 0v-239l80-80v319h-80Zm160 0v-400l80-80v480h-80ZM120-327v-113l280-280 160 160 280-280v113L560-447 400-607 120-327Z',
  dispatch:  'M480-40v-80h280v-40H600v-320h160v-40q0-116-82-198t-198-82q-116 0-198 82t-82 198v40h160v320H200q-33 0-56.5-23.5T120-240v-280q0-74 28.5-139.5T226-774q49-49 114.5-77.5T480-880q74 0 139.5 28.5T734-774q49 49 77.5 114.5T840-520v400q0 33-23.5 56.5T760-40H480ZM200-240h80v-160h-80v160Zm480 0h80v-160h-80v160ZM200-400h80-80Zm480 0h80-80Z',
  tasks:     'm438-240 226-226-58-58-169 169-84-84-57 57 142 142ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Zm280-520v-200H240v640h480v-440H520ZM240-800v200-200 640-640Z',
  reports:   'M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-507h560v-133H200v133Zm0 214h560v-134H200v134Zm0 213h560v-133H200v133Zm40-454v-80h80v80h-80Zm0 214v-80h80v80h-80Zm0 214v-80h80v80h-80Z',
  users:     'M40-160v-112q0-34 17.5-62.5T104-378q62-31 126-46.5T360-440q66 0 130 15.5T616-378q29 15 46.5 43.5T680-272v112H40Zm720 0v-120q0-44-24.5-84.5T666-434q51 6 96 20.5t84 35.5q36 20 55 44.5t19 53.5v120H760ZM360-480q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 66-47 113t-113 47Zm400-160q0 66-47 113t-113 47q-11 0-28-2.5t-28-5.5q27-32 41.5-71t14.5-81q0-42-14.5-81T544-792q14-5 28-6.5t28-1.5q66 0 113 47t47 113ZM120-240h480v-32q0-11-5.5-20T580-306q-54-27-109-40.5T360-360q-56 0-111 13.5T140-306q-9 5-14.5 14t-5.5 20v32Zm240-320q33 0 56.5-23.5T440-640q0-33-23.5-56.5T360-720q-33 0-56.5 23.5T280-640q0 33 23.5 56.5T360-560Zm0 320Zm0-400Z',
  settings:  'm370-80-16-128q-13-5-24.5-12T307-235l-119 50L78-375l103-78q-1-7-1-13.5v-27q0-6.5 1-13.5L78-585l110-190 119 50q11-8 23-15t24-12l16-128h220l16 128q13 5 24.5 12t22.5 15l119-50 110 190-103 78q1 7 1 13.5v27q0 6.5-2 13.5l103 78-110 190-118-50q-11 8-23 15t-24 12L590-80H370Zm70-80h79l14-106q31-8 57.5-23.5T639-327l99 41 39-68-86-65q5-14 7-29.5t2-31.5q0-16-2-31.5t-7-29.5l86-65-39-68-99 42q-22-23-48.5-38.5T533-694l-13-106h-79l-14 106q-31 8-57.5 23.5T321-633l-99-41-39 68 86 64q-5 15-7 30t-2 32q0 16 2 31t7 30l-86 65 39 68 99-42q22 23 48.5 38.5T427-266l13 106Zm42-180q58 0 99-41t41-99q0-58-41-99t-99-41q-59 0-99.5 41T342-480q0 58 40.5 99t99.5 41Zm-2-140Z',
  chevronL:  'M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z',
  chevronR:  'M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z',
  logout:    'M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h280v80H200v560h280v80H200Zm440-160-55-58 102-102H360v-80h327L585-622l55-58 200 200-200 200Z',
  user:      'M234-276q51-39 114-61.5T480-360q69 0 132 22.5T726-276q35-41 54.5-93T800-480q0-133-93.5-226.5T480-800q-133 0-226.5 93.5T160-480q0 59 19.5 111t54.5 93Zm246-164q-59 0-99.5-40.5T340-580q0-59 40.5-99.5T480-720q59 0 99.5 40.5T620-580q0 59-40.5 99.5T480-440Zm0 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q53 0 100-15.5t86-44.5q-39-29-86-44.5T480-280q-53 0-100 15.5T294-220q39 29 86 44.5T480-160Zm0-360q26 0 43-17t17-43q0-26-17-43t-43-17q-26 0-43 17t-17 43q0 26 17 43t43 17Zm0-60Zm0 360Z',
  menu:      'M120-240v-80h720v80H120Zm0-200v-80h720v80H120Zm0-200v-80h720v80H120Z',
  x:         'm256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z',
}

// ── Nav link definition ───────────────────────────────────────────────────────

// modules: undefined = Core (always visible); string[] = requires one of those modules
const NAV_LINKS = [
  { to: '/dashboard',      label: 'Dashboard',     icon: 'dashboard', roles: ['admin', 'company_admin', 'executive'],                                             modules: undefined },
  { to: '/analytics',      label: 'Analitika',     icon: 'analytics', roles: ['admin', 'company_admin', 'analyst'],                                               modules: ['meter_reading', 'scada'] },
  { to: '/reports',        label: 'Izveštaji',     icon: 'reports',   roles: ['admin', 'company_admin', 'analyst', 'executive'],                                  modules: ['meter_reading', 'scada'] },
  { to: '/meters',         label: 'Vodomeri',      icon: 'meters',    roles: ['admin', 'company_admin', 'executive', 'dispatcher', 'meter_reader', 'analyst'],    modules: ['meter_reading'] },
  { to: '/rute',           label: 'Rute i reoni',  icon: 'routes',    roles: ['admin', 'company_admin', 'dispatcher'],                                            modules: ['meter_reading'] },
  { to: '/tasks',          label: 'Moji nalozi',   icon: 'tasks',     roles: ['meter_reader', 'repair_crew', 'scada_operator', 'scada_repair', 'dispatcher', 'admin', 'company_admin'], modules: ['meter_reading', 'repair'] },
  { to: '/dispatch',       label: 'Dispečer',      icon: 'dispatch',  roles: ['admin', 'company_admin', 'dispatcher'],                                            modules: ['meter_reading', 'repair'] },
  { to: '/infrastruktura', label: 'Infrastruktura', icon: 'scada',    roles: ['admin', 'company_admin', 'executive', 'scada_operator', 'scada_repair', 'dispatcher'], modules: ['scada'] },
  { to: '/users',          label: 'Radnici',       icon: 'users',     roles: ['admin', 'company_admin'],                                                           modules: undefined },
  { to: '/settings',       label: 'Podešavanja',   icon: 'settings',  roles: ['admin', 'company_admin'],                                                                               modules: undefined },
] as const

// ── Page title map ────────────────────────────────────────────────────────────

const PAGE_TITLES: Record<string, { title: string; subtitle: string }> = {
  '/dashboard': { title: 'Executive Dashboard', subtitle: 'Pregled mreže za distribuciju vode' },
  '/infrastruktura': { title: 'Infrastruktura',  subtitle: 'Automatski nadzor, zone i mreža' },
  '/rute':           { title: 'Rute i reoni',    subtitle: 'Redosled očitavanja i servisnih reona' },
  '/meters':    { title: 'Vodomeri',             subtitle: 'Pregled i upravljanje vodomerima' },
  '/analytics': { title: 'Analitika',            subtitle: 'Analiza potrošnje i gubici' },
  '/dispatch':  { title: 'Dispečer',             subtitle: 'Radni nalozi i koordinacija ekipa' },
  '/tasks':     { title: 'Moji nalozi',          subtitle: 'Radni nalozi dodeljeni meni' },
  '/reports':   { title: 'Izveštaji',            subtitle: 'Izvoz podataka i mesečna potrošnja' },
  '/users':     { title: 'Radnici',              subtitle: 'Upravljanje radnicima i pristupima' },
  '/operator':  { title: 'Operator Panel',       subtitle: 'I-SOFT interno — upravljanje tenantima' },
  '/admin':     { title: 'Admin Panel',          subtitle: 'I-SOFT interno — upravljanje tenantima' },
  '/settings':  { title: 'Podešavanja',          subtitle: 'Podešavanja kompanije' },
  '/account':   { title: 'Moj nalog',            subtitle: 'Profil i lozinka' },
}

// ── Sidebar content (shared between desktop and mobile overlay) ───────────────

function SidebarContent({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean
  onNavigate?: () => void
}) {
  const { user, refreshToken, logout } = useAuthStore()
  const navigate = useNavigate()

  const visibleLinks = NAV_LINKS.filter((l) => {
    const roleOk = user?.roles.some((r) => l.roles.includes(r as never))
    if (!roleOk) return false
    if (!l.modules || user?.roles.includes('admin')) return true
    return l.modules.some((m) => user?.modules?.includes(m))
  })

  const handleLogout = () => {
    // Best-effort: revoke the session server-side (docs/plan-security-
    // hardening-1.md §2) so a refresh token left on this device is dead,
    // not just unused. Local state clears regardless of the outcome.
    if (refreshToken) authApi.logout(refreshToken).catch(() => {})
    logout()
    navigate('/login')
    onNavigate?.()
  }

  return (
    <>
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-slate-700/60 shrink-0 ${
        collapsed ? 'justify-center px-0' : ''
      }`}>
        <div className="shrink-0 w-12 h-12 flex items-center justify-center">
          <img src="/water-icon.svg" alt="I-SOFT Waterworks" className="w-12 h-12" />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <p className="text-base font-bold text-white leading-tight truncate">I-SOFT</p>
            <p className="text-sm text-slate-400 leading-tight truncate">Waterworks</p>
          </div>
        )}
      </div>

      {/* Nav links */}
      <nav className="flex-1 overflow-y-auto py-4 space-y-0.5 px-2">
        {visibleLinks.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            onClick={onNavigate}
            title={collapsed ? l.label : undefined}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-2.5 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-brand-cyan text-brand-midnight shadow-xs'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              } ${collapsed ? 'justify-center' : ''}`
            }
          >
            <Icon path={ICONS[l.icon as keyof typeof ICONS]} className="w-5 h-5 shrink-0" />
            {!collapsed && <span>{l.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User + logout */}
      <div className={`border-t border-slate-700/60 p-3 space-y-1 shrink-0 ${
        collapsed ? 'flex flex-col items-center' : ''
      }`}>
        {!collapsed && user && (
          <Link to="/account" className="block px-2 py-1.5 rounded-lg hover:bg-slate-800 transition-colors" onClick={onNavigate}>
            <p className="text-xs font-semibold text-slate-200 truncate">{user.fullName}</p>
            <p className="text-xs text-slate-500 truncate">{user.roles[0]}</p>
          </Link>
        )}
        {!collapsed && (
          <div className="flex items-center justify-between px-2 text-[11px] text-slate-500">
            <span>v{__APP_VERSION__}</span>
            <Link to="/privacy" className="hover:text-slate-300 transition-colors" onClick={onNavigate}>
              Politika privatnosti
            </Link>
          </div>
        )}
        <button
          onClick={handleLogout}
          title="Odjava"
          className={`flex items-center gap-3 w-full rounded-lg px-2.5 py-2 text-sm text-slate-400 hover:bg-slate-800 hover:text-red-400 transition-colors ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <Icon path={ICONS.logout} className="w-5 h-5 shrink-0" />
          {!collapsed && <span>Odjava</span>}
        </button>
      </div>
    </>
  )
}

// ── License banner ────────────────────────────────────────────────────────────
// docs/plan-license-expiry-read-only.md. The backend is the actual gate (423
// on a write); this is purely informational, driven by whatever login/
// refresh/activate last returned.

const LICENSE_WARNING_DAYS = 30

function LicenseBanner() {
  const { licenseActive, licenseExpiresAt } = useAuthStore()

  if (!licenseActive) {
    return (
      <div className="shrink-0 bg-red-50 border-b border-red-200 px-4 sm:px-6 py-2 text-sm text-red-800">
        <strong className="font-semibold">Licenca je istekla.</strong> Moguć je samo pregled i izvoz podataka — kontaktirajte I-SOFT.
      </div>
    )
  }

  if (licenseExpiresAt) {
    const daysLeft = Math.ceil((new Date(licenseExpiresAt).getTime() - Date.now()) / 86_400_000)
    if (daysLeft <= LICENSE_WARNING_DAYS) {
      return (
        <div className="shrink-0 bg-amber-50 border-b border-amber-200 px-4 sm:px-6 py-2 text-sm text-amber-800">
          Licenca ističe {daysLeft <= 0 ? 'danas' : daysLeft === 1 ? 'sutra' : `za ${daysLeft} dana`} — kontaktirajte I-SOFT za obnovu.
        </div>
      )
    }
  }

  return null
}

// ── AppShell ──────────────────────────────────────────────────────────────────

export default function AppShell({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const { user } = useAuthStore()
  const { pathname } = useLocation()

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false) }, [pathname])

  // Close mobile drawer on Escape
  useEffect(() => {
    if (!mobileOpen) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileOpen(false) }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [mobileOpen])

  const page = PAGE_TITLES[pathname] ?? { title: 'I-SOFT Waterworks', subtitle: '' }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100">

      {/* ── Desktop sidebar (hidden on mobile) ── */}
      <aside className={`hidden md:flex flex-col shrink-0 bg-slate-900 text-slate-100 transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-60'
      }`}>
        <SidebarContent collapsed={collapsed} />

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="flex items-center justify-center py-3 border-t border-slate-700/60 text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors shrink-0"
          title={collapsed ? 'Proširi meni' : 'Skupi meni'}
        >
          <Icon path={collapsed ? ICONS.chevronR : ICONS.chevronL} className="w-4 h-4" />
        </button>
      </aside>

      {/* ── Mobile overlay drawer ── */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <aside className="md:hidden fixed inset-y-0 left-0 z-50 flex flex-col w-72 bg-slate-900 text-slate-100 shadow-2xl">
            {/* Close button */}
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800"
            >
              <Icon path={ICONS.x} className="w-5 h-5" />
            </button>
            <SidebarContent collapsed={false} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      {/* ── Main area ── */}
      <div className="flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden">

        {/* Top header */}
        <header className="shrink-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between shadow-xs gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {/* Hamburger — mobile only */}
            <button
              className="md:hidden shrink-0 p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              onClick={() => setMobileOpen(true)}
              aria-label="Otvori meni"
            >
              <Icon path={ICONS.menu} className="w-5 h-5" />
            </button>

            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 leading-tight truncate">
                {page.title}
              </h1>
              {page.subtitle && (
                <p className="hidden sm:block text-xs text-gray-500 mt-0.5">{page.subtitle}</p>
              )}
            </div>
          </div>

          {/* User chip */}
          {user && (
            <Link to="/account" className="flex items-center gap-2 pl-3 border-l border-gray-200 shrink-0 hover:opacity-75 transition-opacity">
              {/* account_circle already draws its own circle+person shape -- a
                  separate bg-blue-100 rounded-full wrapper around a small copy of
                  it produced two mismatched circles nested inside each other.
                  Just render it at the badge's full footprint instead. */}
              <Icon path={ICONS.user} className="w-8 h-8 text-brand-cyan-text shrink-0" />
              <div className="hidden sm:block text-right">
                <p className="text-sm font-semibold text-gray-900 leading-tight">{user.fullName}</p>
                <p className="text-xs text-gray-500 leading-tight">{user.roles[0]}</p>
              </div>
            </Link>
          )}
        </header>

        <LicenseBanner />

        {/* Page content */}
        <main className="flex-1 min-h-0 overflow-auto bg-gray-50">
          {children}
        </main>
      </div>
    </div>
  )
}
